import React from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useLanguageStore } from '../../stores/languageStore'
import { useAuthStore } from '../../stores/authStore'
import { useAnalytics } from '../../hooks/useAnalytics'
import { SupportedLanguage } from '@tvia-ai/shared'

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage, getLanguageOptions } = useLanguageStore()
  const { user } = useAuthStore()
  const languageOptions = getLanguageOptions()
  const analytics = useAnalytics()

  const handleLanguageChange = (newLanguage: string) => {
    const selectedLanguage = newLanguage as SupportedLanguage
    const previousLanguage = language
    
    // Track language change
    analytics.trackNavigation.languageChange(previousLanguage, selectedLanguage)
    
    // Update local state and UI language immediately
    setLanguage(selectedLanguage)
    
    // Only try to save language preference to backend if user is authenticated
    if (user) {
      const { changeLanguage } = useAuthStore.getState()
      changeLanguage(selectedLanguage).catch(error => {
        console.error('Failed to save language preference to server:', error)
        analytics.trackError(new Error('Language preference update failed'), 'language_update_error', 'user_settings')
        // Language switching still works in UI even if backend save fails
      })
    }
  }

  const currentLanguageOption = languageOptions.find(option => option.code === language)

  return (
    <Menu as="div" className="relative">
      <div>
        <Menu.Button className="flex items-center text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 px-3 py-3 rounded-lg text-sm font-medium min-h-[44px] transition-all">
          <GlobeAltIcon className="h-5 w-5 mr-2" />
          <span className="hidden sm:block mr-1">
            {currentLanguageOption?.flag} {currentLanguageOption?.name}
          </span>
          <span className="sm:hidden">
            {currentLanguageOption?.flag}
          </span>
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        </Menu.Button>
      </div>
      
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className={`absolute ${currentLanguageOption?.isRTL ? 'left-0' : 'right-0'} mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50`}>
          <div className="py-1">
            {languageOptions.map((option) => (
              <Menu.Item key={option.code}>
                {({ active }) => (
                  <button
                    onClick={() => handleLanguageChange(option.code)}
                    className={`${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    } ${
                      option.code === language ? 'bg-blue-50 text-blue-600' : ''
                    } group flex items-center w-full px-4 py-3 text-sm ${
                      option.isRTL ? 'text-right' : 'text-left'
                    } min-h-[44px] transition-all hover:bg-gray-50`}
                    dir={option.isRTL ? 'rtl' : 'ltr'}
                  >
                    <span className="mr-3 text-lg">{option.flag}</span>
                    <span className="flex-1">{option.name}</span>
                    {option.code === language && (
                      <span className="ml-2 text-blue-600">✓</span>
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default LanguageSwitcher 